import { Toaster } from "sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

// Immediate redirect: admin subdomain root → dashboard (runs before React mounts)
if (typeof window !== 'undefined' && window.location.hostname === 'admin.moon-laundry.com' && (window.location.pathname === '/' || window.location.pathname === '')) {
  window.location.replace('/admin/dashboard');
}
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import { AppProvider } from '@/lib/AppContext';

// Page imports
import CustomerPage from './pages/CustomerPage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminOffers from './pages/AdminOffers';
import AdminCoupons from './pages/AdminCoupons';
import AdminVerify from './pages/AdminVerify';
import AdminCustomers from './pages/AdminCustomers';
import AdminSettings from './pages/AdminSettings';
import AdminImportCustomers from './pages/AdminImportCustomers';
import LaundryCustomerPage from './pages/LaundryCustomerPage';
import LaundryOrders from './pages/LaundryOrders';
import LaundryDelegates from './pages/LaundryDelegates';
import LaundryServices from './pages/LaundryServices';
import LandingPage from './pages/LandingPage';
import RatingPage from './pages/RatingPage';
import AdminReviews from './pages/AdminReviews';
import AdminPromos from './pages/AdminPromos';
import AdminOfferBanners from './pages/AdminOfferBanners';
import AdminUsers from './pages/AdminUsers';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import Error400 from './pages/Error400';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img
            src="https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png"
            alt="مغاسل القمر الأبيض"
            className="w-20 h-20 object-contain animate-pulse"
          />
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <Routes>
      {/* Landing Page - Main Home Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/wheel" element={<CustomerPage />} />
      <Route path="/rate" element={<RatingPage />} />

      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/offers" element={<AdminOffers />} />
      <Route path="/admin/coupons" element={<AdminCoupons />} />
      <Route path="/admin/verify" element={<AdminVerify />} />
      <Route path="/admin/customers" element={<AdminCustomers />} />
      <Route path="/admin/settings" element={<AdminSettings />} />
      <Route path="/admin/import" element={<AdminImportCustomers />} />

      {/* Laundry Routes */}
      <Route path="/laundry" element={<LaundryCustomerPage />} />
      <Route path="/admin/laundry/orders" element={<LaundryOrders />} />
      <Route path="/admin/laundry/delegates" element={<LaundryDelegates />} />
      <Route path="/admin/laundry/services" element={<LaundryServices />} />
      <Route path="/admin/reviews" element={<AdminReviews />} />
      <Route path="/admin/promos" element={<AdminPromos />} />
      <Route path="/admin/offer-banners" element={<AdminOfferBanners />} />
      <Route path="/admin/users" element={<AdminUsers />} />

      <Route path="/error400" element={<Error400 />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AppProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <AuthenticatedApp />
          </Router>
          <Toaster position="top-center" richColors closeButton />
        </QueryClientProvider>
      </AuthProvider>
    </AppProvider>
  )
}

export default App